import {
  extractSentryEventStatusCode,
  extractSentryEventUrl,
  type SentryEventLike,
} from './sentryEventFields';

// Matches /api/v2/users.json, /api/v3/users.json, /api/v4/users.json — every
// registration/signup call site (RegisterForm, RegisterForm.web,
// RegisterWithPhoneNumberForm, SignUpWithEmail.web, InvitationPreview.web,
// ScopedSignupModal.web, OnboardingModal) posts to one of these.
//
// Anchored to a whole path segment, and to the end of the path, so a bare
// substring test cannot widen it: /api/v3/not-users.json and
// /api/v3/users.json/something are NOT registration endpoints and must keep
// reporting. `failedRequestTargets` is global, so anything this matches is
// dropped for every request in the app.
const REGISTRATION_URL_PATTERN = /\/api\/v\d+\/users\.json(?:[?#]|$)/;

/**
 * Drops the `HTTP Client Error with status code: 422` events from
 * `POST .../users.json` (Sentry FRONTEND-2Z0, TA-10917).
 *
 * A 422 here is a normal registration validation failure (duplicate email,
 * weak password, etc.) — every registration form already reads it via
 * `parseRegisterResponse` + `getRegisterFieldErrors` and maps it onto inline
 * field errors, the expected path, not a crash. Sentry's httpClientIntegration
 * tags these `handled: no` because it flags any non-2xx response regardless of
 * whether app code processes it afterward in a `.then()` chain, so the event
 * carries no signal to act on.
 *
 * Deliberately narrow: only the `/api/v{N}/users.json` registration endpoints,
 * and only 422. A 422 anywhere else (e.g. task/project mutations returning
 * validation errors over REST) is not covered by this contract and still
 * reports.
 */
export const shouldDropHandledRegistration422 = (
  event: SentryEventLike
): boolean => {
  const url = extractSentryEventUrl(event);
  const statusCode = extractSentryEventStatusCode(event);

  return REGISTRATION_URL_PATTERN.test(url) && statusCode === 422;
};
